import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  CCard,
  CCardBody,
  CCardHeader,
  CButton,
  CSpinner,
  CTable,
  CTableBody,
  CTableHead,
  CTableRow,
  CTableHeaderCell,
  CTableDataCell,
  CFormInput,
  CTooltip,
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
  CFormSelect,
} from "@coreui/react";
import CIcon from '@coreui/icons-react';
import { cilCopy, cilTrash } from '@coreui/icons';
import { fetchAllSkills, fetchSkillById, apiService } from "../services/api";

// Utility function to create a unique ID
const createUniqueId = (skill, index) => {
  return `skill-${skill.skillId}-${skill.status || 'unknown'}-${index}`;
};

const SkillList = ({ limit = null }) => {
  const navigate = useNavigate();
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [skillId, setSkillId] = useState("");
  const [skillStage, setSkillStage] = useState("development");
  const [skillInfo, setSkillInfo] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState(null);
  const [showLookupResult, setShowLookupResult] = useState(false);
  const [copiedId, setCopiedId] = useState("");
  
  // Delete operation states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [skillToDelete, setSkillToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [deleteSuccess, setDeleteSuccess] = useState('');

  const fetchSkills = async () => {
    setLoading(true);
    setError(null);
    try {
      // Call the real API
      const data = await fetchAllSkills();
      
      // Sort skills by lastUpdated date (most recent first) and then by skillId alphabetically
      const sortedData = [...(data || [])].sort((a, b) => {
        // First sort by lastUpdated date (most recent first)
        const dateComparison = new Date(b.lastUpdated) - new Date(a.lastUpdated);
        
        // If dates are the same, sort by skillId (alphabetically)
        if (dateComparison === 0) {
          return a.skillId.localeCompare(b.skillId);
        }
        
        return dateComparison;
      });
      
      setSkills(sortedData);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to fetch skills");
      setSkills([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSkillInfo = async () => {
    if (!skillId) {
      setLookupError("Please enter a Skill ID");
      return;
    }
    setLookupLoading(true);
    setLookupError(null);
    setSkillInfo(null);
    try {
      const data = await fetchSkillById(skillId, skillStage);
      setSkillInfo(data);
      setShowLookupResult(true);
    } catch (err) {
      setLookupError(
        err.response?.data?.error || "Failed to fetch skill information"
      );
      setSkillInfo(null);
    } finally {
      setLookupLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        setCopiedId(text);
        // Reset copied ID after 2 seconds
        setTimeout(() => setCopiedId(""), 2000);
      })
      .catch(err => {
        console.error('Failed to copy: ', err);
      });
  };
  
  const handleDeleteClick = (skill) => {
    setSkillToDelete(skill);
    setShowDeleteModal(true);
    setDeleteError(null);
    setDeleteSuccess('');
  };
  
  const handleConfirmDelete = async () => {
    if (!skillToDelete) return;
    
    setDeleteLoading(true);
    setDeleteError(null);
    setDeleteSuccess('');
    
    try {
      // First get an access token
      const authResponse = await apiService.authenticateWithAmazon();
      const { accessToken } = authResponse;
      
      // Then delete the skill
      await apiService.deleteSkill(accessToken, skillToDelete.skillId);
      
      // Update the skills list to remove the deleted skill
      setSkills(prevSkills => prevSkills.filter(s => s.skillId !== skillToDelete.skillId));
      setDeleteSuccess(`Skill "${skillToDelete.name || skillToDelete.skillId}" was successfully deleted.`);
      
      // Close the modal after a short delay to show the success message
      setTimeout(() => {
        setShowDeleteModal(false);
        setSkillToDelete(null);
      }, 2000);
    } catch (err) {
      console.error('Delete skill error:', err);
      setDeleteError(`Failed to delete skill: ${err.response?.data?.error || err.message}`);
    } finally {
      setDeleteLoading(false);
    }
  };
  
  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setSkillToDelete(null);
    setDeleteError(null);
  };

  // Fetch skills on mount
  useEffect(() => {
    fetchSkills();
  }, []);

  // Add a unique ID to each skill to ensure React rendering is stable
  // This approach guarantees no duplicate keys, even with identical skill objects
  const processedSkills = skills.map((skill, index) => ({
    ...skill,
    // Add a unique ID that combines skillId, status and array position
    _uid: createUniqueId(skill, index)
  }));
  
  // Limit the number of skills to show if limit is provided
  const displaySkills = limit ? processedSkills.slice(0, parseInt(limit)) : processedSkills;

  // Format skill status for display
  const getStatusDisplay = (status) => {
    return status ? status.toLowerCase() : 'development';
  };

  return (
    <CCard className="mb-4">
      <CCardHeader className="d-flex justify-content-between align-items-center">
        <strong>{limit ? 'Recent' : 'All'} Alexa Skills</strong>
        {!showLookupResult && (
          <CButton
            color="secondary"
            size="sm"
            onClick={fetchSkills}
            disabled={loading}
          >
            {loading ? <CSpinner size="sm" /> : "Refresh"}
          </CButton>
        )}
        {showLookupResult && (
          <CButton
            color="secondary"
            size="sm"
            onClick={() => {
              setShowLookupResult(false);
              setSkillInfo(null);
              setSkillId("");
            }}
          >
            Back to List
          </CButton>
        )}
      </CCardHeader>
      <CCardBody>
        {/* Lookup Section */}
        {!limit && (
          <div className="d-flex flex-column flex-sm-row mb-3">
            <CFormInput
              type="text"
              placeholder="Enter Skill ID"
              value={skillId}
              onChange={(e) => setSkillId(e.target.value)}
              className="mb-2 mb-sm-0 me-sm-2"
              style={{ width: "100%" }}
            />
            <CFormSelect
              value={skillStage}
              onChange={(e) => setSkillStage(e.target.value)}
              className="mb-2 mb-sm-0 me-sm-2"
              style={{ width: "160px" }}
            >
              <option value="development">Development</option>
              <option value="live">Live</option>
            </CFormSelect>
            <CButton
              color="primary"
              onClick={fetchSkillInfo}
              disabled={lookupLoading}
            >
              {lookupLoading ? <CSpinner size="sm" /> : "Lookup"}
            </CButton>
          </div>
        )}
        
        {lookupError && <div className="alert alert-danger mt-3">{lookupError}</div>}
        {!showLookupResult && (
          <>
            {error && (
              <div className="alert alert-danger mt-3">{error}</div>
            )}
            <div className="table-responsive">
              <CTable hover className="mb-0">
                <CTableHead>
                  <CTableRow>
                    <CTableHeaderCell>ID</CTableHeaderCell>
                    <CTableHeaderCell>Name</CTableHeaderCell>
                    <CTableHeaderCell>Stage</CTableHeaderCell>
                    <CTableHeaderCell className="text-center">Actions</CTableHeaderCell>
                  </CTableRow>
                </CTableHead>
                <CTableBody>
                  {displaySkills.length === 0 && !loading && (
                    <CTableRow>
                      <CTableDataCell colSpan={4} className="text-center">
                        No skills found.
                      </CTableDataCell>
                    </CTableRow>
                  )}
                  {loading && (
                    <CTableRow>
                      <CTableDataCell colSpan={4} className="text-center">
                        <CSpinner size="sm" /> Loading skills...
                      </CTableDataCell>
                    </CTableRow>
                  )}
                  {!loading && displaySkills.map((skill) => (
                    <CTableRow 
                      key={skill._uid} 
                      className="cursor-pointer" 
                      onClick={() => navigate(`/skill-detail?id=${skill.skillId}&stage=${skill.stage || 'development'}`)} 
                      style={{ cursor: 'pointer' }}
                    >
                      <CTableDataCell className="text-truncate" style={{ maxWidth: '500px' }}>
                        <div className="d-flex align-items-center">
                          {skill.skillId}
                          <CTooltip content={copiedId === skill.skillId ? "Copied!" : "Copy Skill ID"}>
                            <CButton 
                              color="light" 
                              size="sm" 
                              className="ms-2"
                              onClick={(e) => {
                                e.stopPropagation();
                                copyToClipboard(skill.skillId);
                              }}
                            >
                              <CIcon icon={cilCopy} size="sm" />
                            </CButton>
                          </CTooltip>
                        </div>
                      </CTableDataCell>
                      <CTableDataCell>{skill.name || "Unnamed Skill"}</CTableDataCell>
                      <CTableDataCell>{getStatusDisplay(skill.status)}</CTableDataCell>
                      <CTableDataCell className="text-center">
                        <CTooltip content="Delete Skill">
                          <CButton 
                            color="danger" 
                            size="sm"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteClick(skill);
                            }}
                          >
                            <CIcon icon={cilTrash} size="sm" />
                          </CButton>
                        </CTooltip>
                      </CTableDataCell>
                    </CTableRow>
                  ))}
                </CTableBody>
              </CTable>
            </div>

            {/* Delete Confirmation Modal */}
            <CModal
              visible={showDeleteModal}
              onClose={handleCancelDelete}
              alignment="center"
            >
              <CModalHeader>
                <CModalTitle>Confirm Deletion</CModalTitle>
              </CModalHeader>
              <CModalBody>
                {deleteLoading ? (
                  <div className="text-center py-3">
                    <CSpinner color="danger" />
                    <p className="mt-2">Deleting skill...</p>
                  </div>
                ) : (
                  <>
                    {deleteSuccess ? (
                      <div className="alert alert-success">{deleteSuccess}</div>
                    ) : (
                      <>
                        <p>Are you sure you want to delete this skill?</p>
                        <div className="mb-3">
                          <strong>Skill Name:</strong> {skillToDelete?.name || 'Unnamed Skill'}
                        </div>
                        <div className="mb-3">
                          <strong>Skill ID:</strong> {skillToDelete?.skillId}
                        </div>
                        <div className="alert alert-warning">
                          <strong>Warning:</strong> This action cannot be undone. The skill will be permanently deleted from your Amazon developer account.
                        </div>
                        {deleteError && <div className="alert alert-danger">{deleteError}</div>}
                      </>
                    )}
                  </>
                )}
              </CModalBody>
              {!deleteLoading && !deleteSuccess && (
                <CModalFooter>
                  <CButton color="secondary" onClick={handleCancelDelete}>
                    Cancel
                  </CButton>
                  <CButton color="danger" onClick={handleConfirmDelete}>
                    Delete Skill
                  </CButton>
                </CModalFooter>
              )}
            </CModal>
          </>
        )}

        {/* Skill Detail View */}


        {showLookupResult && skillInfo && (
          <div className="mt-4">
            <h4>{skillInfo.manifest?.publishingInformation?.locales?.["en-US"]?.name || "Unnamed Skill"}</h4>
            <div className="table-responsive">
              <table className="table table-striped">
                <tbody>
                  <tr>
                    <th>Skill ID</th>
                    <td>
                      <div className="d-flex align-items-center">
                        {skillInfo.skillId}
                        <CTooltip content={copiedId === skillInfo.skillId ? "Copied!" : "Copy Skill ID"}>
                          <CButton 
                            color="light" 
                            size="sm" 
                            className="ms-2"
                            onClick={() => copyToClipboard(skillInfo.skillId)}
                          >
                            <CIcon icon={cilCopy} size="sm" />
                          </CButton>
                        </CTooltip>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <th>Skill Name</th>
                    <td>
                      {skillInfo.manifest?.publishingInformation
                        ?.locales?.["en-US"]?.name || "-"}
                    </td>
                  </tr>
                  <tr>
                    <th>Description</th>
                    <td>
                      {skillInfo.manifest?.publishingInformation
                        ?.locales?.["en-US"]?.description || "-"}
                    </td>
                  </tr>
                  <tr>
                    <th>Short Description</th>
                    <td>
                      {skillInfo.manifest?.publishingInformation
                        ?.locales?.["en-US"]?.summary || "-"}
                    </td>
                  </tr>
                  <tr>
                    <th>Status</th>
                    <td>
                      {skillInfo.status ||
                      skillInfo.manifest?.publishingInformation
                        ?.distributionCountries
                        ? "Live"
                        : "-"}
                    </td>
                  </tr>
                  <tr>
                    <th>Invocation Name</th>
                    <td>
                      {skillInfo.manifest?.apis?.custom?.endpoint
                        ?.uri || "-"}
                    </td>
                  </tr>
                  <tr>
                    <th>Example Phrases</th>
                    <td>
                      {(
                        skillInfo.manifest?.publishingInformation
                          ?.locales?.["en-US"]?.examplePhrases || []
                      ).map((phrase, idx) => (
                        <div key={idx}>{phrase}</div>
                      ))}
                    </td>
                  </tr>
                  <tr>
                    <th>Keywords</th>
                    <td>
                      {(
                        skillInfo.manifest?.publishingInformation
                          ?.locales?.["en-US"]?.keywords || []
                      ).join(", ") || "-"}
                    </td>
                  </tr>
                  <tr>
                    <th>Category</th>
                    <td>
                      {skillInfo.manifest?.publishingInformation
                        ?.category || "-"}
                    </td>
                  </tr>
                  <tr>
                    <th>Privacy Policy URL</th>
                    <td>
                      {skillInfo.manifest?.privacyAndCompliance
                        ?.locales?.["en-US"]?.privacyPolicyUrl || "-"}
                    </td>
                  </tr>
                  <tr>
                    <th>Small Icon</th>
                    <td>
                      {skillInfo.manifest?.publishingInformation
                        ?.locales?.["en-US"]?.smallIconUri ? (
                        <img
                          src={
                            skillInfo.manifest.publishingInformation
                              .locales["en-US"].smallIconUri
                          }
                          alt="Small Icon"
                          style={{ maxHeight: 64 }}
                        />
                      ) : (
                        "-"
                      )}
                    </td>
                  </tr>
                  <tr>
                    <th>Large Icon</th>
                    <td>
                      {skillInfo.manifest?.publishingInformation
                        ?.locales?.["en-US"]?.largeIconUri ? (
                        <img
                          src={
                            skillInfo.manifest.publishingInformation
                              .locales["en-US"].largeIconUri
                          }
                          alt="Large Icon"
                          style={{ maxHeight: 128 }}
                        />
                      ) : (
                        "-"
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}
      </CCardBody>
    </CCard>
  );
};

export default SkillList;
